/**
 * useFieldStateManager Hook
 *
 * Manages field state tracking for additional questions:
 * - Dirty state tracking for unsaved changes
 * - Initial field state management
 * - Field change detection
 * - Clean state restoration after form submission
 */

import * as React from 'react';
import { IFormField } from '@ViewModels';
import { FieldStateManager } from '../types';

/**
 * Custom hook for managing field state and dirty tracking
 *
 * @param fields - Current array of form fields
 * @param isClone - Whether this is a cloned form (affects saved state detection)
 * @returns FieldStateManager with state and utility functions
 */
export const useFieldStateManager = (fields: IFormField<string>[], _isClone = false): FieldStateManager => {
	// Custom dirty tracking state
	const [dirtyFields, setDirtyFields] = React.useState<Set<string>>(new Set());

	// Track initial field states to prevent false dirty marking
	const [initialFieldStates, setInitialFieldStates] = React.useState<Map<string, IFormField<string>>>(new Map());

	// Flag to track if we're in the initial setup phase
	const [isInitialSetup, setIsInitialSetup] = React.useState(true);

	// Initialize states when fields are first provided, but keep initial setup active
	React.useEffect(() => {
		if (isInitialSetup && fields.length > 0 && initialFieldStates.size === 0) {
			// Use microtask to ensure this runs after the current execution context
			Promise.resolve().then(() => {
				const newStates = new Map<string, IFormField<string>>();
				fields.forEach(field => {
					newStates.set(field.id, { ...field });
				});
				setInitialFieldStates(newStates);
			});
		}
	}, [fields, isInitialSetup, initialFieldStates.size]);

	// Reset dirty state when form is reset after successful submission
	React.useEffect(() => {
		// If we have fields and they all have _type (meaning they're saved), clear dirty state
		const allFieldsSaved = fields.length > 0 && fields.every(field => field._type);

		if (allFieldsSaved) {
			// End initial setup when fields are saved
			if (isInitialSetup) {
				setIsInitialSetup(false);
			}
			// Clear dirty state when all fields are saved (form was successfully submitted)
			setDirtyFields(new Set());
			// Update initial states to current saved state
			const updatedInitialStates = new Map<string, IFormField<string>>();
			fields.forEach(field => {
				updatedInitialStates.set(field.id, { ...field });
			});
			setInitialFieldStates(updatedInitialStates);
		}
	}, [fields, isInitialSetup]);

	// Helper function to check if a field is dirty (has unsaved changes)
	const isFieldDirty = React.useCallback(
		(fieldId: string, _fieldIndex: number): boolean => {
			// Check if the field is in our custom dirty tracking
			return dirtyFields.has(fieldId);
		},
		[dirtyFields]
	);

	// Use ref to track if initial setup is complete for synchronous access
	const isInitialSetupRef = React.useRef(isInitialSetup);
	isInitialSetupRef.current = isInitialSetup;

	// Function to mark a field as dirty
	const markFieldDirty = React.useCallback((fieldId: string) => {
		if (!isInitialSetupRef.current) {
			setDirtyFields(prev => {
				const newDirtyFields = new Set(prev);
				newDirtyFields.add(fieldId);
				return newDirtyFields;
			});
		}
	}, []);

	// Function to check if a field has actually changed from its initial state
	const hasFieldChanged = React.useCallback(
		(field: IFormField<string>): boolean => {
			const initialField = initialFieldStates.get(field.id);
			if (!initialField) return true; // New field, consider it changed

			return (
				field.label !== initialField.label ||
				field.isOptional !== initialField.isOptional ||
				field.fieldType !== initialField.fieldType ||
				JSON.stringify(field.options) !== JSON.stringify(initialField.options)
			);
		},
		[initialFieldStates]
	);

	// Function to conditionally mark field as dirty based on actual changes
	const markFieldDirtyIfChanged = React.useCallback(
		(field: IFormField<string>) => {
			const isActuallyDirty = hasFieldChanged(field);

			if (isActuallyDirty && !isInitialSetupRef.current) {
				markFieldDirty(field.id);
			}
		},
		[hasFieldChanged, markFieldDirty]
	);

	// Function to reset all dirty state
	const resetDirtyState = React.useCallback(() => {
		setDirtyFields(new Set());
		// End initial setup when dirty state is reset (simulates form completion)
		if (isInitialSetupRef.current) {
			setIsInitialSetup(false);
			isInitialSetupRef.current = false; // Update ref immediately for synchronous access
		}
	}, []);

	// Function to update initial states (useful when fields are programmatically updated)
	const updateInitialStates = React.useCallback((updatedFields: IFormField<string>[]) => {
		const updatedInitialStates = new Map<string, IFormField<string>>();
		updatedFields.forEach(field => {
			updatedInitialStates.set(field.id, { ...field });
		});
		setInitialFieldStates(updatedInitialStates);
	}, []);

	return {
		dirtyFields,
		initialFieldStates,
		isInitialSetup,
		isFieldDirty,
		markFieldDirty,
		resetDirtyState,
		updateInitialStates,
		hasFieldChanged,
		markFieldDirtyIfChanged,
	};
};
